import { useMemo } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import dayjs from "dayjs";
import { CollectorDTO, UtilityBillByIdDTO, DocumentByIdDTO } from "api";
import { MaybeNull, UtilityBillFormData } from "types";
import { definedValueOrDefault } from "utils";
import { getCollectorString } from "pages/authenticated/documents/utils";
import { getIsWarningFields } from "../utils";

interface UseUtilityBillFormParams {
  isCreateMode: boolean;
  document?: DocumentByIdDTO;
  utilityBill?: UtilityBillByIdDTO;
  collectorsFromSiteData?: CollectorDTO[];
  newCollectorId?: number;
  currentSite: { id: number | undefined; name: MaybeNull<string> };
  isSiteChanged: boolean;
}

export const useUtilityBillForm = ({
  isCreateMode,
  document,
  utilityBill,
  collectorsFromSiteData,
  newCollectorId,
  currentSite,
  isSiteChanged,
}: UseUtilityBillFormParams): UseFormReturn<UtilityBillFormData> & {
  defaultValues: UtilityBillFormData;
  isWarningFields: Record<keyof UtilityBillFormData, boolean>;
} => {
  const defaultValues = useMemo(() => {
    if (isCreateMode) {
      return {
        siteName: document?.siteName ?? "",
        provider: "",
        measuredUnit: "",
        measuredValue: "",
        startDate: null,
        endDate: null,
        amount: "",
        currency: "",
        meterNumber: "",
        accountNumber: "",
        utilityTypeName: "",
        collector: getCollectorString({
          accountNumber: "-",
          utilityTypeName: "-",
          meterNumber: "-",
        } as CollectorDTO),
        subtype: "",
        traveledMiles: "",
      };
    }

    const selectedCollector = collectorsFromSiteData?.find(
      (c: any) => c.id === (newCollectorId ?? utilityBill?.collectorId),
    );

    return {
      siteName: definedValueOrDefault(currentSite.name ?? utilityBill?.siteName, ""),
      provider: selectedCollector?.providerName ?? "",
      measuredUnit: utilityBill?.measuredUnit ?? "",
      measuredValue: definedValueOrDefault(
        utilityBill?.measuredValue?.toString().replace(/,/g, "."),
        "",
        String,
      ),
      startDate: dayjs(utilityBill?.startDate, "YYYY-MM-DD"),
      endDate: dayjs(utilityBill?.endDate, "YYYY-MM-DD"),
      amount: definedValueOrDefault(
        utilityBill?.amount?.toString().replace(/,/g, "."),
        "",
        String,
      ),
      currency: utilityBill?.currency ?? "",
      meterNumber: definedValueOrDefault(
        selectedCollector?.meterNumber ?? utilityBill?.meterNumber,
        "",
      ),
      accountNumber: definedValueOrDefault(
        selectedCollector?.accountNumber ?? utilityBill?.accountNumber,
        "",
      ),
      utilityTypeName: definedValueOrDefault(
        selectedCollector?.utilityTypeName ?? utilityBill?.type,
        "",
      ),
      collector: selectedCollector
        ? getCollectorString(selectedCollector)
        : utilityBill?.collectorId && !isSiteChanged
          ? getCollectorString({
            accountNumber: utilityBill?.accountNumber,
            utilityTypeName: utilityBill?.type,
            meterNumber: utilityBill?.meterNumber,
          } as CollectorDTO)
          : getCollectorString({
            accountNumber: "-",
            utilityTypeName: "-",
            meterNumber: "-",
          } as CollectorDTO),
      subtype: definedValueOrDefault(
        selectedCollector ? selectedCollector.utilitySubType : utilityBill?.subtype,
        "",
      ),
      traveledMiles: definedValueOrDefault(utilityBill?.milesTraveled, "", String),
    };
  }, [
    isCreateMode,
    document,
    utilityBill,
    collectorsFromSiteData,
    newCollectorId,
    currentSite,
    isSiteChanged,
  ]);

  const form = useForm<UtilityBillFormData>({
    defaultValues,
    mode: "onChange",
  });

  const watchValues = form.watch();
  const isWarningFields = getIsWarningFields(defaultValues, watchValues, {
    collector: "- / - / -",
  });

  return {
    ...form,
    defaultValues,
    isWarningFields,
  };
};

